import { z } from 'zod'
import { ApiError } from './client'

/**
 * The list envelope used by the cluster-scoped mgmt endpoints:
 *
 *     { TStamp, count, message, status, data: { result: [...] } }
 *
 * Note this is NOT the `{ data }` envelope the /lc/users/* auth endpoints use.
 *
 * `count` is the server's total. On device-management it is the total across
 * all pages and exceeds `result.length`; on stream-management, which ignores
 * pagination params, it equals it. Callers must not assume either.
 */
export const resultEnvelope = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    count: z.number().default(0),
    message: z.string().optional(),
    status: z.string().optional(),
    TStamp: z.string().optional(),
    data: z.object({ result: z.array(item).default([]) }),
  })

/**
 * The OTHER list envelope, where `data` is the array ITSELF:
 *
 *     { status, count, message?, TStamp?, data: [...] }
 *
 * Used by the plugin endpoints, the platform user list, and the scope user
 * directory. `count` is the server's total across all pages.
 *
 * The difference from `resultEnvelope` is silent and costly: `data.result` on
 * this payload is `undefined`, so a permissive parse renders an empty page
 * rather than failing. Which envelope an endpoint uses is not predictable from
 * its prefix — /lc/mgmt serves this one for user-list and the `{ data }` auth
 * shape for /lc/users/* — so it is established per endpoint, from a capture.
 */
export const flatCountEnvelope = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    count: z.number().default(0),
    message: z.string().optional(),
    status: z.string().optional(),
    TStamp: z.string().optional(),
    data: z.array(item).default([]),
  })

/**
 * The acknowledgement a WRITE returns: `{ status, message }`, no payload.
 *
 * Parsing it is not enough. `checkEnvelopeStatus` in the client is
 * deliberately narrow — it intercepts `status: 'invalid'` and the one
 * unauthorized case, because only those need to reach the app as an interrupt
 * rather than as data. Everything else, including a plain
 * `200 { status: 'failed', message: '…' }`, flows through as a RESOLVED
 * promise.
 *
 * For a read that is survivable: the caller renders nothing. For a write it is
 * not — the mutation resolves, the screen reports the change landed, and it
 * did not. Every legacy handler branches on `status === 'success'` explicitly
 * for exactly this reason.
 *
 * So writes parse with `ackEnvelope` and then call `assertAck`. Broadening the
 * client's own check instead would change the behaviour of every read in the
 * app, which is a bigger decision than this.
 */
export const ackEnvelope = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
  })
  .passthrough()

export type Ack = z.output<typeof ackEnvelope>

/**
 * Throw unless the server said the write succeeded.
 *
 * A response with no `status` at all is accepted: some endpoints answer with a
 * bare 200, and refusing those would break writes that do work. Only an
 * explicit non-success is treated as failure.
 */
export function assertAck(res: Ack, what: string): void {
  if (res.status !== undefined && res.status !== 'success') {
    throw new ApiError(400, res.message || `${what} did not succeed.`, res, res.message)
  }
}
